import { useMemo } from "react";
import { BookingService } from "@/services/BookingService";
import { useQuery } from "@tanstack/react-query";
import type { BookingFilters, BookingSort } from "@/types/Booking";
import type { PaginatedResponse } from "@/types/Pagination";
import type { Booking } from "@/types/Booking";

interface UseBookingsQueryOptions {
  showBaseOnly?: boolean;
  filters?: BookingFilters;
  sort?: BookingSort;
  enabled?: boolean;
  staleTime?: number;
  skip?: number;
  limit?: number;
  refreshKey?: number;
}

export function useBookingsQuery(options: UseBookingsQueryOptions = {}) {
  const {
    showBaseOnly = true,
    filters,
    sort,
    enabled = true,
    staleTime = 5 * 60 * 1000, // 5 minutes
    skip = 0,
    limit = 50,
    refreshKey = 0,
  } = options;

  const bookingService = useMemo(() => new BookingService(), []);

  return useQuery<PaginatedResponse<Booking>>({
    queryKey: ["bookings", showBaseOnly, filters, sort, skip, limit, refreshKey],
    queryFn: () => bookingService.getAllBookings(showBaseOnly, filters, sort, skip, limit),
    enabled,
    staleTime,
    refetchOnWindowFocus: false,
    retry: 2,
    retryDelay: (attemptIndex) => Math.min(1000 * 2 ** attemptIndex, 30000), // Exponential backoff up to 30 seconds
  });
}
